import { apiClient } from '@/auth';

/**
 * Goal target catalog item — `GET /goal_targets` (MNT swagger:5329, schema
 * `GoalTargetResponse` swagger:18094). The catalog is global; clinicians pick
 * a `target_name` from it when creating a goal.
 */
export type GoalTarget = {
  goal_type: 'nutrient' | 'frequency';
  target_name: string;
  target_display_name: string;
  target_unit?: string | null;
};

export type GoalChange = 'increase' | 'decrease';

/**
 * Period the goal is measured over. Per MNT swagger:18444 (CreateGoalRequest),
 * the enum covers daily/weekly aggregates and per-meal-slot variants:
 *   - `day` / `week`: rolling totals across the period
 *   - `breakfast` / `lunch` / `dinner` / `snack`: one meal-slot per day
 *   - `meal`: applies to the subset of meals listed in `meal_slots`
 *     (default = breakfast/lunch/dinner if `meal_slots` omitted)
 */
export type GoalPeriod = 'day' | 'week' | 'breakfast' | 'lunch' | 'dinner' | 'snack' | 'meal';

/** Subset of meal slots a `period: 'meal'` goal applies to. */
export type GoalMealSlot = 'breakfast' | 'lunch' | 'dinner' | 'snack';

/**
 * Range-style target type. When set, the goal is interpreted relative to
 * `target_range_low` / `target_range_high` rather than a single target_value:
 *   - `above`: target_range_low is the floor (≥)
 *   - `below`: target_range_high is the ceiling (≤)
 *   - `within`: both bounds form an inclusive window
 * Per MNT swagger:18450.
 */
export type GoalRangeType = 'above' | 'below' | 'within';

/**
 * Row of `GET /patients/{id}/goals/extended_weekly_report` (swagger:18212,
 * `GoalExtendedWeeklyReportResponse`). One per active goal, with progress
 * stats over the requested date range. v1 renders the goals tab from this
 * endpoint via `appApiGoalGetGoalsProgressReport`.
 */
export type GoalRow = {
  patient_id: number;
  goal_id: number;
  goal_type: 'nutrient' | 'frequency';
  change: GoalChange;
  target_name: string;
  target_display_name: string;
  target_unit?: string | null;
  target_value: number;
  period: GoalPeriod;
  /** When `period === 'meal'`, the subset of meal slots the goal applies
   *  to. Null/undefined means the server default (breakfast/lunch/dinner). */
  meal_slots?: GoalMealSlot[] | null;
  /** Range-style target metadata. See `GoalRangeType`. */
  target_range_type?: GoalRangeType | null;
  target_range_low?: number | null;
  target_range_high?: number | null;
  start_date: string;
  end_date: string;
  dates_exclude?: string[] | null;
  range_value?: number;
  range_success_count: number;
  range_total_count: number;
  missed_logging_opportunities?: number;
  statement: string;
  goal_image_url?: string | null;
};

/**
 * Body for POST /patients/{id}/goals (`CreateGoalRequest` swagger:18102) and
 * PUT /patients/{id}/goals/{goal_id} (`UpdateGoalRequest` swagger:18111).
 * Both have the same shape — all five fields required.
 */
export type GoalInput = {
  goal_type: 'nutrient' | 'frequency';
  change: GoalChange;
  target_name: string;
  target_value: number;
  period: GoalPeriod;
  /** Required-ish when `period === 'meal'`. Server defaults to
   *  breakfast/lunch/dinner when omitted. */
  meal_slots?: GoalMealSlot[] | null;
  target_range_type?: GoalRangeType | null;
  target_range_low?: number | null;
  target_range_high?: number | null;
};

/**
 * Snapshot row from `GET /patients/{id}/goals?date=YYYY-MM-DD`
 * (MNT swagger:5345, schema `GoalProgressResponse` swagger:18149).
 *
 * Note this differs from `GoalRow`:
 *   - `id` here, not `goal_id`
 *   - has `current_value`, no `range_*` count fields
 *   - no `start_date`/`end_date` (it's a single-date snapshot, not a range)
 *
 * Used as the authoritative goal-list source because it never depends on
 * a date range — `progress_report` and `extended_weekly_report` both 5xx
 * for some patient/range combinations, leaving the tab blank.
 */
export type GoalSnapshot = {
  id: number;
  patient_id: number;
  goal_type: 'nutrient' | 'frequency';
  change: GoalChange;
  target_name: string;
  target_display_name: string;
  target_unit?: string | null;
  target_value: number;
  current_value: number;
  period: GoalPeriod;
  meal_slots?: GoalMealSlot[] | null;
  target_range_type?: GoalRangeType | null;
  target_range_low?: number | null;
  target_range_high?: number | null;
  statement: string;
  goal_image_url?: string | null;
};

export const goalsApi = {
  /** Catalog of available goal targets — global, doesn't depend on patient. */
  async targets(): Promise<GoalTarget[]> {
    const res = await apiClient.get<GoalTarget[]>(`/goal_targets`);
    return Array.isArray(res.data) ? res.data : [];
  },

  /**
   * Snapshot of the patient's active goals on a single calendar date. Stable
   * — doesn't depend on any progress-window aggregation server-side, so it's
   * the safe choice for the canonical goal list. Use today's date to get
   * "everything currently active."
   */
  async snapshot(patientId: number, date: string): Promise<GoalSnapshot[]> {
    const res = await apiClient.get<GoalSnapshot[]>(`/patients/${patientId}/goals`, {
      params: { date },
    });
    return Array.isArray(res.data) ? res.data : [];
  },

  /**
   * Goals + progress over the date range. v1's goals service
   * (clinician/src/services/goal.ts:22) calls this via `appApiGoalGetGoalsProgressReport`.
   * BOTH `progress_report` and `extended_weekly_report` return the same shape
   * (`GoalExtendedWeeklyReportResponse`) but observed 500s on staging/prod
   * for some patient/range combos as of 2026-05-07. Callers should treat
   * this as best-effort and fall back to the snapshot list.
   */
  async list(patientId: number, dateSince: string, dateUntil: string): Promise<GoalRow[]> {
    const res = await apiClient.get<GoalRow[]>(
      `/patients/${patientId}/goals/progress_report`,
      { params: { date_since: dateSince, date_until: dateUntil } },
    );
    return Array.isArray(res.data) ? res.data : [];
  },

  async create(patientId: number, body: GoalInput): Promise<unknown> {
    const res = await apiClient.post(`/patients/${patientId}/goals`, body);
    return res.data;
  },

  async update(patientId: number, goalId: number, body: GoalInput): Promise<unknown> {
    const res = await apiClient.put(`/patients/${patientId}/goals/${goalId}`, body);
    return res.data;
  },

  async remove(patientId: number, goalId: number): Promise<void> {
    await apiClient.delete(`/patients/${patientId}/goals/${goalId}`);
  },
};
